import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, User, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const POLLINATIONS_API_URL = 'https://gen.pollinations.ai/v1/chat/completions';
const POLLINATIONS_TOKEN = '6_XSlDCFa2XHOANX';

const FAQ_DATA = {
  he: [
    {
      keywords: ['מחיר', 'עלות', 'כמה עולה', 'תשלום'],
      answer: 'כל יצירת תמונה עולה קרדיט אחד. אתם מקבלים 5 קרדיטים בחינם בהרשמה! חבילות נוספות זמינות בעמוד המחירים.'
    },
    {
      keywords: ['קרדיט', 'קרדיטים', 'credit'],
      answer: 'קרדיטים הם המטבע שלנו ליצירת תמונות. כל תמונה עולה קרדיט אחד. הקרדיטים לעולם לא פגים!'
    },
    {
      keywords: ['זמן', 'כמה זמן', 'מהירות', 'לוקח'],
      answer: 'יצירת תמונה אחת לוקחת בדרך כלל 15-30 שניות. התהליך מהיר ואוטומטי לחלוטין.'
    },
    {
      keywords: ['איכות', 'רזולוציה', 'גודל'],
      answer: 'התמונות נוצרות באיכות גבוהה של 8K, מושלמות להדפסה ושיתוף ברשתות החברתיות.'
    },
    {
      keywords: ['החזר', 'ביטול', 'כסף חזרה'],
      answer: 'אנו מציעים החזר כספי מלא תוך 7 ימים אם לא השתמשתם בקרדיטים שרכשתם.'
    },
    {
      keywords: ['מסחרי', 'עסקי', 'רישיון'],
      answer: 'חבילת Pro כוללת רישיון מסחרי שמאפשר שימוש בתמונות למטרות עסקיות. חבילות אחרות הן לשימוש אישי בלבד.'
    },
    {
      keywords: ['הפניה', 'חברים', 'referral'],
      answer: 'שתפו את קוד ההפניה שלכם! כשחבר קונה קרדיטים, אתם מקבלים 5 קרדיטים בחינם והחבר מקבל 10 קרדיטים בונוס!'
    },
    {
      keywords: ['בטיחות', 'פרטיות', 'תמונות'],
      answer: 'התמונות שלכם מאובטחות ופרטיות. אנחנו לא משתפים אותן עם אף אחד ואתם שולטים לחלוטין במה שקורה איתן.'
    },
    {
      keywords: ['דמויות', 'אפשרויות', 'כמה'],
      answer: 'יש לנו למעלה מ-80 דמויות מגוונות! מגיבורי-על, דמויות היסטוריות, מקצועות, פנטזיה ועוד הרבה יותר.'
    },
    {
      keywords: ['עזרה', 'תמיכה', 'בעיה'],
      answer: 'אפשר לפנות אלינו דרך עמוד יצירת קשר או לשלוח מייל ל-support@aiphotobooth.com. נשמח לעזור!'
    },
    {
      keywords: ['תוצאה', 'לא טובה', 'בעיה בתמונה'],
      answer: 'אם התוצאה לא מושלמת, נסו שוב עם תמונה אחרת או זווית שונה. תמונות פנים ברורות עם תאורה טובה נותנות את התוצאות הטובות ביותר.'
    }
  ],
  en: [
    {
      keywords: ['price', 'cost', 'how much', 'payment'],
      answer: 'Each image creation costs 1 credit. You get 5 free credits when you sign up! Additional packages are available on the pricing page.'
    },
    {
      keywords: ['credit', 'credits'],
      answer: 'Credits are our currency for creating images. Each image costs 1 credit. Credits never expire!'
    },
    {
      keywords: ['time', 'how long', 'speed', 'take'],
      answer: 'Creating one image usually takes 15-30 seconds. The process is fast and fully automatic.'
    },
    {
      keywords: ['quality', 'resolution', 'size'],
      answer: 'Images are created in high 8K quality, perfect for printing and sharing on social media.'
    },
    {
      keywords: ['refund', 'cancel', 'money back'],
      answer: 'We offer a full refund within 7 days if you haven\'t used the credits you purchased.'
    },
    {
      keywords: ['commercial', 'business', 'license'],
      answer: 'The Pro package includes a commercial license for business use. Other packages are for personal use only.'
    },
    {
      keywords: ['referral', 'friends', 'invite'],
      answer: 'Share your referral code! When a friend buys credits, you get 5 free credits and they get 10 bonus credits!'
    },
    {
      keywords: ['safety', 'privacy', 'photos'],
      answer: 'Your photos are secure and private. We don\'t share them with anyone and you have full control over them.'
    },
    {
      keywords: ['characters', 'options', 'how many'],
      answer: 'We have over 80 diverse characters! From superheroes, historical figures, professions, fantasy and much more.'
    },
    {
      keywords: ['help', 'support', 'problem'],
      answer: 'You can contact us through the contact page or email support@aiphotobooth.com. We\'re happy to help!'
    },
    {
      keywords: ['result', 'not good', 'image problem'],
      answer: 'If the result isn\'t perfect, try again with a different photo or angle. Clear face photos with good lighting give the best results.'
    }
  ]
};

export default function ChatBot() {
  const { language, isRTL } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const getInitialGreeting = () => ({
    type: 'bot',
    text: language === 'he' 
      ? 'שלום! 👋 אני כאן לעזור לכם עם כל שאלה על AI PhotoBooth. מה תרצו לדעת?'
      : 'Hello! 👋 I\'m here to help you with any questions about AI PhotoBooth. What would you like to know?'
  });

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([getInitialGreeting()]);
    }
  }, [isOpen, language]);

  const resetChat = () => {
    setMessages([getInitialGreeting()]);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const findLocalAnswer = (query) => {
    const faqData = FAQ_DATA[language] || FAQ_DATA.en;
    const lowerQuery = query.toLowerCase();
    
    for (const faq of faqData) {
      for (const keyword of faq.keywords) {
        if (lowerQuery.includes(keyword.toLowerCase())) {
          return faq.answer;
        }
      }
    }
    return null;
  };

  const getAIResponse = async (query) => {
    const systemPrompt = language === 'he' 
      ? `אתה עוזר ידידותי של AI PhotoBooth - אפליקציה שמאפשרת להפוך תמונות של אנשים לדמויות שונות (גיבורי-על, מקצועות, דמויות היסטוריות וכו') באמצעות בינה מלאכותית.

מידע חשוב על האפליקציה:
- כל יצירת תמונה עולה קרדיט אחד
- משתמשים חדשים מקבלים 5 קרדיטים בחינם
- קרדיטים לעולם לא פגים
- יש למעלה מ-80 דמויות שונות
- זמן יצירה: 15-30 שניות
- איכות 8K
- תוכנית הפניות: 5 קרדיטים כשחבר קונה
- החזר כספי תוך 7 ימים אם לא השתמשת

ענה בקצרה וידידותית בעברית.`
      : `You are a friendly assistant for AI PhotoBooth - an app that transforms people's photos into different characters (superheroes, professions, historical figures, etc.) using AI.

Key information:
- Each image creation costs 1 credit
- New users get 5 free credits
- Credits never expire
- Over 80 different characters available
- Creation time: 15-30 seconds
- 8K quality
- Referral program: 5 credits when a friend purchases
- 7-day refund if unused

Answer briefly and friendly in English.`;

    try {
      const response = await fetch(POLLINATIONS_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${POLLINATIONS_TOKEN}`
        },
        body: JSON.stringify({
          model: 'openai',
          messages: [
            { role: 'system', content: systemPrompt },
            ...messages.map(m => ({
              role: m.type === 'user' ? 'user' : 'assistant',
              content: m.text
            })),
            { role: 'user', content: query }
          ],
          max_tokens: 500
        })
      });

      const data = await response.json();
      return data.choices?.[0]?.message?.content || (language === 'he' 
        ? 'מצטער, לא הצלחתי לעבד את השאלה. נסו שוב.'
        : 'Sorry, I couldn\'t process the question. Please try again.');
    } catch (error) {
      console.error('AI API error:', error);
      return language === 'he'
        ? 'מצטער, יש בעיה בחיבור. נסו שוב מאוחר יותר.'
        : 'Sorry, connection issue. Please try again later.';
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = { type: 'user', text: inputValue };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // First try local FAQ
    const localAnswer = findLocalAnswer(inputValue);
    
    let botResponse;
    if (localAnswer) {
      botResponse = { type: 'bot', text: localAnswer };
    } else {
      // Use AI for more complex questions
      const aiAnswer = await getAIResponse(inputValue);
      botResponse = { type: 'bot', text: aiAnswer };
    }

    setMessages(prev => [...prev, botResponse]);
    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  const quickQuestions = language === 'he' 
    ? ['כמה עולה?', 'מה זה קרדיטים?', 'כמה דמויות יש?', 'איך עובדת ההפניה?']
    : ['How much?', 'What are credits?', 'How many characters?', 'How does referral work?'];

  return (
    <>
      {/* Chat Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 ${isRTL ? 'left-6' : 'right-6'} z-50 w-14 h-14 rounded-full bg-gradient-to-r from-violet-600 to-pink-600 text-white shadow-lg flex items-center justify-center ${isOpen ? 'hidden' : ''}`}
      >
        <MessageCircle className="w-6 h-6" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className={`fixed bottom-6 ${isRTL ? 'left-6' : 'right-6'} z-50 w-[360px] max-w-[calc(100vw-48px)] bg-slate-900 rounded-2xl shadow-2xl border border-slate-800 overflow-hidden`}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-600 to-pink-600 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">
                    {language === 'he' ? 'עוזר AI PhotoBooth' : 'AI PhotoBooth Assistant'}
                  </h3>
                  <p className="text-xs text-white/70">
                    {language === 'he' ? 'תמיד כאן לעזור' : 'Always here to help'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={resetChat} 
                  className="text-white/70 hover:text-white p-1"
                  title={language === 'he' ? 'התחל מחדש' : 'Start over'}
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button onClick={() => setIsOpen(false)} className="text-white/70 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="h-80 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                      msg.type === 'user'
                        ? 'bg-violet-600 text-white'
                        : 'bg-slate-800 text-slate-200'
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-slate-800 text-slate-200 rounded-2xl px-4 py-2.5 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {language === 'he' ? 'חושב...' : 'Thinking...'}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Questions */}
            {messages.length === 1 && (
              <div className="px-4 pb-2">
                <p className="text-xs text-slate-500 mb-2">
                  {language === 'he' ? 'שאלות נפוצות:' : 'Quick questions:'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {quickQuestions.map((q, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setInputValue(q);
                        setTimeout(() => handleSend(), 100);
                      }}
                      className="text-xs px-3 py-1.5 rounded-full bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-slate-800">
              <div className="flex gap-2">
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={language === 'he' ? 'שאלו אותי משהו...' : 'Ask me something...'}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  disabled={isLoading}
                />
                <Button
                  onClick={handleSend}
                  disabled={isLoading}
                  className="bg-violet-600 hover:bg-violet-700 text-white px-3"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
